import React, { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import TopNavigation from "./TopNavigation";
import Sidebar from "./sidebar/Sidebar";
import PageSkeleton from "./skeletons/PageSkeleton";
import WorkspaceTabBar from "./layout/WorkspaceTabBar";
import { isElectronApp } from "../utils/utils";

const normalizeRole = (role) =>
  role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
const isChiefAdmin = (user) => {
  const role = user?.role_name || user?.role;
  return normalizeRole(role) === "chief-admin";
};

const ProtectedLayout = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const [sidebarCollapsed, setSidebarColapsed] = useState(false);
  const { refreshTheme } = useTheme();
  const location = useLocation();

  const isClientsListingPage = /^\/clients\/?$/.test(location.pathname);
  const isProjectsListingPage = /^\/projects\/?$/.test(location.pathname);
  const electronMode = isElectronApp();

  // Load user theme when authenticated (covers post-login and page refresh)
  useEffect(() => {
    if (isAuthenticated) {
      refreshTheme();
    }
  }, [isAuthenticated, refreshTheme]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageSkeleton />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const layout = (
    <div className="flex h-[100dvh] min-h-0 w-full min-w-0 overflow-hidden">
      <Sidebar setParentColapsed={setSidebarColapsed} />
      <div
        className="relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
        style={{
          background: "var(--color-bg-secondary)",
          width: `calc(100% - ${sidebarCollapsed ? 60 : 210}px)`,
        }}
      >
        <div className="absolute left-0 right-0 top-0 z-50">
          <TopNavigation />
        </div>
        {electronMode && (
          <div className="absolute left-0 right-0 top-16 z-40">
            <WorkspaceTabBar />
          </div>
        )}
        <main
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain bg-[var(--color-bg-secondary)] ${
            electronMode ? "pt-[calc(4rem+2.5rem+1px)]" : "pt-[calc(4rem+1px)]"
          }`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );

  return layout;
};

export default ProtectedLayout;
